import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "../store"
import { BrandOptions, FilterID, FilterIDs, Thread, Threads } from "../../types"
import { DateRangePreset } from "../../common/ui/activity/date-filter"

interface ConversationState {
  selectedBrandID?: number
  imagePreview?: string
  currentThread?: Thread
  showCurrentThreadDetailsView: boolean
  currentFilter: FilterIDs
  mergedThreads: Threads // New: Store merged threads in Redux
  searchQuery: string
  brandOptions?: BrandOptions
  hasMore: boolean
  dateRangePreset: DateRangePreset
  customDateStart: number | null
  customDateEnd: number | null
}

const initialState: ConversationState = {
  showCurrentThreadDetailsView: false,
  currentFilter: [],
  mergedThreads: [],
  searchQuery: "",
  brandOptions: [],
  hasMore: false,
  dateRangePreset: "all",
  customDateStart: null,
  customDateEnd: null
}

const conversationsSlice = createSlice({
  name: "conversations",
  initialState,
  reducers: {
    setSelectedBrandID(state, action: PayloadAction<number | undefined>) {
      state.selectedBrandID = action.payload
    },
    clearSelectedBrandID(state) {
      state.selectedBrandID = undefined
    },
    setImagePreview(state, action: PayloadAction<string | undefined>) {
      state.imagePreview = action.payload
    },
    clearImagePreview(state) {
      state.imagePreview = undefined
    },
    setCurrentThread(state, action: PayloadAction<Thread | undefined>) {
      state.currentThread = action.payload
    },
    toggleCurrentThreadDetailsView(state) {
      state.showCurrentThreadDetailsView = !state.showCurrentThreadDetailsView
    },
    clearCurrentThread(state) {
      state.currentThread = undefined
    },
    setFilters(state, action: PayloadAction<FilterID>) {
      const value = action.payload
      state.currentFilter = state.currentFilter.includes(value)
        ? state.currentFilter.filter(f => f !== value)
        : [...state.currentFilter, value]
    },
    setMergedThreads(state, action: PayloadAction<Threads>) {
      state.mergedThreads = action.payload // Update merged threads in Redux
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload
    },
    setBrandOptions(state, action: PayloadAction<BrandOptions>) {
      state.brandOptions = action.payload
    },
    setHasMore(state, action: PayloadAction<boolean>) {
      state.hasMore = action.payload
    },
    appendMergedThreads(state, action: PayloadAction<Threads>) {
      const existingIds = new Set(state.mergedThreads.map(t => t._id))
      const newThreads = action.payload.filter(t => !existingIds.has(t._id))
      state.mergedThreads = [...state.mergedThreads, ...newThreads]
    },
    setDateRangePreset(state, action: PayloadAction<DateRangePreset>) {
      state.dateRangePreset = action.payload
      state.mergedThreads = []
      if (action.payload !== "custom") {
        state.customDateStart = null
        state.customDateEnd = null
      }
    },
    setCustomDateRange(
      state,
      action: PayloadAction<{ start: number; end: number }>
    ) {
      state.dateRangePreset = "custom"
      state.customDateStart = action.payload.start
      state.customDateEnd = action.payload.end
      state.mergedThreads = []
    }
  }
})

export const {
  setSelectedBrandID,
  clearSelectedBrandID,
  setImagePreview,
  clearImagePreview,
  setCurrentThread,
  toggleCurrentThreadDetailsView,
  clearCurrentThread,
  setSearchQuery,
  setFilters: setConversationsFilter,
  setMergedThreads,
  setBrandOptions,
  setHasMore,
  appendMergedThreads,
  setDateRangePreset,
  setCustomDateRange
} = conversationsSlice.actions

// Selector to get the selectedBrandID value
export const selectSelectedBrandID = (state: RootState) =>
  state.conversations.selectedBrandID
export const imagePreview = (state: RootState) =>
  state.conversations.imagePreview
export const currentThread = (state: RootState) =>
  state.conversations.currentThread
export const showCurrentThreadDetailsView = (state: RootState) =>
  state.conversations.showCurrentThreadDetailsView
export const currentFilter = (state: RootState) =>
  state.conversations.currentFilter
export const selectMergedThreads = (state: RootState) =>
  state.conversations.mergedThreads
export const searchQuery = (state: RootState) => state.conversations.searchQuery
export const brandOptions = (state: RootState) =>
  state.conversations.brandOptions
export const selectHasMore = (state: RootState) => state.conversations.hasMore
export const selectDateRangePreset = (state: RootState) =>
  state.conversations.dateRangePreset
export const selectCustomDateStart = (state: RootState) =>
  state.conversations.customDateStart
export const selectCustomDateEnd = (state: RootState) =>
  state.conversations.customDateEnd

export default conversationsSlice.reducer
